import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Icon, Text, useDebouncedFn } from "@jsq/ds/core";
import { useFormContext, FormTextInput } from "@jsq/ds/forms";
import { addAction } from "@jsq/observability";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { useValidateRoutingNumberLazyQuery } from "../../../../../graphql/supergraph.index";
import { PaymentsRoutingNumberType } from "../../../../../graphql/supergraphOperations";
import { type HandleValidateParams } from "../../../hooks/use-bank-validation";
import {
  ROUTING_NUMBER_LENGTH,
  RoutingNumberInputFields,
  type RoutingNumberInputType,
} from "./constants";

interface FormFieldKeysType {
  routingNumber: string;
  bankName: string;
}

export interface RoutingNumberInputProps {
  useAbaPrefix?: boolean;
  formFieldKeys?: FormFieldKeysType;
  onValidationCompleted: (params: HandleValidateParams) => void;
  onClear: () => void;
}

/**
 * @todo we should verify that the validated bank supports ACH; For example, not all ABA routing numbers work for both ACH and wire payments.
 *     If it does not support ACH we should raise an error. https://junipersquare.atlassian.net/browse/FF-3301
 */
export const RoutingNumberInput: React.FC<RoutingNumberInputProps> = ({
  useAbaPrefix = false,
  formFieldKeys = {
    routingNumber: RoutingNumberInputFields.RoutingNumber,
    bankName: RoutingNumberInputFields.BankName,
  },
  onValidationCompleted,
  onClear,
}) => {
  const [routingNumberField, routingNumbersHelpers] = useField<string>(
    formFieldKeys.routingNumber,
  );
  const { setFieldTouched } = useFormContext<RoutingNumberInputType>();
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);

  /**
   * Clear the local validated bank country if the SWIFT number field is cleared.
   */
  useEffect(() => {
    if (!routingNumberField.value) {
      if (onClear) {
        onClear();
      }
    }
  }, [routingNumberField.value, onClear]);

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data) => {
      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};
      // This is temporary code being implemented here to investigate that validation is working correctly.
      // https://junipersquare.atlassian.net/browse/FF-3509
      addAction("validate_routing_number_query");

      if (onValidationCompleted) {
        // This is temporary code being implemented here to investigate that validation is working correctly.
        // https://junipersquare.atlassian.net/browse/FF-3509
        addAction("on_validate_routing_number_completed");
        onValidationCompleted({
          setShowBankValidatorError,
          bankName,
          bankAddress,
          isValid,
        });
      }
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidateRoutingNumber = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    if (
      routingNumberField?.value?.length === ROUTING_NUMBER_LENGTH &&
      !routingNumbersHelpers.error
    ) {
      debouncedValidateRoutingNumber({
        variables: {
          validateRoutingNumberInput: {
            routingNumber: routingNumberField.value,
            routingNumberType: PaymentsRoutingNumberType.Aba,
          },
        },
      });
    } else {
      // Clear bankName and bankAddress fields
      if (onClear) {
        onClear();
      }
      setShowBankValidatorError(false);
    }
  }, [
    debouncedValidateRoutingNumber,
    formFieldKeys.bankName,
    routingNumberField.value,
    routingNumbersHelpers.error,
    onClear,
  ]);

  return (
    <FieldGroup>
      <MultiInputFormRow>
        <Flex flexDirection="column">
          <FormTextInput
            name={formFieldKeys.routingNumber}
            isFullWidth
            onPaste={() => {
              void setFieldTouched(formFieldKeys.routingNumber, true, true);
            }}
            label={
              <HelpIndicator
                id="routing-number-help-indicator"
                placement="right"
                data-cy="help-icon"
                content="An ABA routing number (also known as routing number
                or routing transfer number) is a sequence of nine numeric
                characters used by banks to identify specific financial
                institutions within the United States."
              >
                <Text>
                  {useAbaPrefix ? "ABA r" : "R"}outing number{" "}
                  <Icon type="question-circle" size="small" variant="outline" />{" "}
                </Text>
              </HelpIndicator>
            }
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized routing number</ErrorMessage>}
        </Flex>
        <FormTextInput name={formFieldKeys.bankName} isFullWidth label="Bank name" />
      </MultiInputFormRow>
    </FieldGroup>
  );
};
